import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Lock } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SAMPLE_PROMPT =
  "Summarize performance of Fatima Al-Thani, QID 28463519204, salary QAR 42,000/month, DOB 1991-03-14, email fatima.t@corp.qa";

const MODELS = [
  { value: "anthropic|claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 (default)" },
  { value: "openai|gpt-5.2", label: "GPT-5.2" },
  { value: "gemini|gemini-3-flash-preview", label: "Gemini 3 Flash" },
];

const TOKEN_RE = /(\[[A-Z]+_\d{3}\])/g;

export const MaskedText = ({ text, testId }) => (
  <pre
    data-testid={testId}
    className="font-mono text-[13px] leading-relaxed whitespace-pre-wrap break-words bg-slate-50 border border-border p-4 text-slate-700"
  >
    {text.split(TOKEN_RE).map((part, i) =>
      TOKEN_RE.test(part) ? (
        <span key={i} className="text-teal-700 bg-teal-50 font-semibold px-0.5">
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      )
    )}
  </pre>
);

export default function GatewayTester({ onActivity }) {
  const [prompt, setPrompt] = useState(SAMPLE_PROMPT);
  const [modelChoice, setModelChoice] = useState(MODELS[0].value);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!prompt.trim()) {
      toast.error("Enter a prompt first");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const [provider, model] = modelChoice.split("|");
      const { data } = await api.post("/ai/gateway/test", { prompt, provider, model });
      setResult(data);
      toast.success(`${data.entities_masked.length} PII entities masked before LLM call`);
      onActivity?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gateway call failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-none border-border shadow-sm" data-testid="gateway-tester-card">
      <CardHeader className="border-b border-border pb-4">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-teal-600" strokeWidth={2.2} />
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.12em] text-primary">
            AI Gateway — PII Masking Proxy
          </CardTitle>
        </div>
        <CardDescription className="text-[13px]">
          PII is tokenized before leaving the platform. The LLM never sees raw identity data.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-5 space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide shrink-0">
            Model
          </span>
          <Select value={modelChoice} onValueChange={setModelChoice}>
            <SelectTrigger className="rounded-none h-9 text-[13px]" data-testid="gateway-model-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {MODELS.map((m) => (
                <SelectItem key={m.value} value={m.value} className="rounded-none text-[13px]">
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Textarea
          data-testid="gateway-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          placeholder="Enter a prompt containing PII..."
          className="rounded-none font-mono text-[13px] resize-none min-h-[110px]"
        />

        <Button
          data-testid="gateway-run-btn"
          onClick={run}
          disabled={loading}
          className="rounded-none bg-teal-700 hover:bg-teal-800 text-white h-9 px-5 text-[13px] font-medium"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          {loading ? "Routing through gateway..." : "Run through Gateway"}
        </Button>

        {result && (
          <div className="space-y-4 fade-in" data-testid="gateway-results">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
                Masked outbound payload
              </p>
              <MaskedText text={result.masked_prompt} testId="gateway-masked-output" />
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
                Entities detected
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="gateway-entities-list">
                {result.entities_masked.map((e, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="rounded-none font-mono text-[11px] border-teal-600/40 text-teal-800 bg-teal-50/60"
                  >
                    {e.type} → {e.token}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
                De-masked LLM response
              </p>
              <div
                data-testid="gateway-llm-response"
                className="text-[13px] leading-relaxed whitespace-pre-wrap border border-border bg-white p-4 text-slate-800 max-h-72 overflow-y-auto"
              >
                {result.llm_response}
              </div>
            </div>

            <p className="text-[11px] font-mono text-muted-foreground" data-testid="gateway-meta">
              {result.model} · {result.latency_ms}ms · audit {result.audit_id.slice(0, 8)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
